import React, { Component } from 'react';
import css from './LeadsAndCustomers.module.scss';
import axios from 'axios';
import _ from "lodash";
import Alert from "../../../components/Alert/Alert";


/**
 * @class
 *
 * Leads and Customers section in Settings page.
 */
const LeadsAndCustomers = class extends Component {
    // ---------- class fields: ----------
    source = axios.CancelToken.source();

    FIELD_LEADS = 'leads';
    FIELD_OPPORTUNITY = 'opportunity';

    // ---------- state: ----------
    state = {
        activeFieldTab: this.FIELD_LEADS,
        leadFields: [],
        opportunityFields: [],
        alertMessage: '',
        shouldShowAlert: false,
    };

    // ---------- life cycle methods: ----------
    componentDidMount() {
        axios.get(
            '/sf/metaData',
            {
                cancelToken: this.source.token
            })
            .then((res) => {
                this.setState({
                    // LeadsAndCustomerFields:
                    leadFields:         this.extractData(res.data, this.FIELD_LEADS),
                    opportunityFields:  this.extractData(res.data, this.FIELD_OPPORTUNITY)
                });
            })
            .catch(err => {
                if (axios.isCancel(err)) {
                    console.log('Request canceled');
                } else {
                    console.log('(GET) Error:\n', err);
                }
            });
    }

    componentWillUnmount() {
        this.source.cancel();
    }

    // ---------- methods: ----------
    /**
     * @method
     * @methodOf LeadsAndCustomers
     *
     * The displayed fields -- selected by the active field tab.
     *
     * @return {array}: Fields.
     */
    displayedFields = () => {
        let fields = [];

        switch(this.state.activeFieldTab) {
            case this.FIELD_LEADS:
                fields = _.cloneDeep(this.state.leadFields);
                break;
            case this.FIELD_OPPORTUNITY:
                fields = _.cloneDeep(this.state.opportunityFields);
                break;
            default:
                break;
        }

        return fields;
    };

    /**
     * @method
     * @methodOf LeadsAndCustomers
     *
     * Extracts and maps the metadata fields from the API response object.
     * Our response object has fields (e.g. `leads` and `opportunity`) -- which we want. In the response object, these individual fields are split into
     *  lists like `visible` and `required` fields -- we want to combine these separate lists into a abject with these properties instead. This helper method
     *  is used for that purpose.
     *
     * @param rawData {object}: The entire response data.
     * @param key {string}: The key in the response object (e.g. `leads` and `opportunity` which are properties of the response object).
     *
     * @return {array}: List of (mapped) metadata fields.
     */
    extractData = (rawData, key) => {
        const fields = [];

        // endpoint parameter name differs from the field property we extract data from:
        let _key;
        switch (key) {
            case this.FIELD_LEADS:
                _key = 'leadFields';
                break;
            case this.FIELD_OPPORTUNITY:
                _key = 'opportunityFields';
                break;
            default:
                break;
        }

        const required = _.map(rawData[_key].required, (field) => {
            return field.label;
        });

        _.each(rawData[_key].visible, (field) => {
            field.visible = true;
            field.required = required.indexOf(field.label) !== -1;
            fields.push(field);
        });

        _.each(rawData[_key].hidden, (field) => {
            field.visible = false;
            field.required = false;
            fields.push(field);
        });

        return fields;
    };

    /**
     * @method
     * @methodOf LeadsAndCustomers
     *
     * Process/maps data to correct data structure for POST request.
     * This is the opposite to `extractData` in that we have to split our data back into separate lists (e.g. `visible`, `hidden`, etc.).
     *
     * @return {object}: Payload object.
     */
    prepPayload = () => {
        const fields = this.displayedFields();

        let result = {
            visible: [],
            hidden: [],
            required: []
        };

        _.each(fields, (field) => {
            const f = {
                name:field.name,
                label:field.label
            };

            if (field.visible) {
                result.visible.push(f);
            } else {
                result.hidden.push(f);
            }

            if (field.required) {
                result.required.push(f);
            }
        });

        return {
            data: result
        };
    };

    /**
     * Updates the selected checkbox for the given active field in the state.
     *
     * @param updatedField
     * @param checkboxType
     */
    checkboxOnChange = (updatedField, checkboxType) => {
        let fields  = this.displayedFields();

        fields = fields.map(field => {
            if (field.name === updatedField.name) {
                const updatedField = {...field};
                updatedField[checkboxType] = !field[checkboxType];
                return updatedField;
            }
            return field;
        });

        switch (this.state.activeFieldTab) {
            case this.FIELD_LEADS:
                this.setState({ leadFields: fields });
                break;
            case this.FIELD_OPPORTUNITY:
                this.setState({ opportunityFields: fields });
                break;
            default:
                break;
        }
    };

    saveSchemeOnClick = () => {
        const payload = this.prepPayload();

        axios.post(
            `/settings/fields/${this.state.activeFieldTab}`,
            payload,
            {
                cancelToken: this.source.token
            })
            .then((res) => {
                this.setState({
                    shouldShowAlert: true,
                    alertMessage: 'Data Saved Successfully.'
                });
                setTimeout(() => {
                    this.setState({
                        shouldShowAlert: false,
                    });
                }, 5500);
            })
            .catch(err => {
                if (axios.isCancel(err)) {
                    console.log('Request canceled');
                } else {
                    console.log('(POST) Error:\n', err);
                }
            });
    };

    /**
     * Sets the active field in the state.
     * Styles and displayed content on the page/view is predicated on the active field.
     *
     * @param field {string}: Name of field to toggle visibility. Fields include: "customer" and "lead".
     * @returns {undefined}: Sets the `activeFieldTab` state.
     */
    handleSetActiveFieldTab = (field) => {
        this.setState({
            activeFieldTab: field
        })
    };

    /**
     * Dynamically sets button CSS based on the currently active field.
     *
     * @param field {string}: Name of field.
     * @returns {string}: generated CSS string.
     */
    activeFieldTabButtonCss = (field) => {
        return this.state.activeFieldTab === field ? 'btn btn-primary' : 'btn btn-light';
    };

    render() {
        return (
            <div className={css.LeadsAndCustomerFields}>
                <Alert showAlert={this.state.shouldShowAlert}>{this.state.alertMessage}</Alert>

                <div className="row">
                    <div className="col-12 col-lg-7">
                        <button
                            className={this.activeFieldTabButtonCss(this.FIELD_LEADS)}
                            type="button"
                            onClick={() => this.handleSetActiveFieldTab(this.FIELD_LEADS)}
                        >Leads</button>
                        <button
                            className={`${this.activeFieldTabButtonCss(this.FIELD_OPPORTUNITY)} ml-1`}
                            type="button"
                            onClick={() => this.handleSetActiveFieldTab(this.FIELD_OPPORTUNITY)}
                        >Customers</button>
                    </div>
                    <div className="col-12 col-lg-5 text-right mt-3 mt-lg-0">
                        <button className="btn btn-primary" type="button" onClick={() => this.saveSchemeOnClick()}>Save {this.state.activeFieldTab} Field Scheme</button>
                    </div>
                </div>

                <ul className="list-group my-4">
                    { this.displayedFields().map((field) => {
                        return (
                            <li key={`${this.state.activeFieldTab}-${field.name}`} className="list-group-item">
                                <div className="row">
                                    <div className="col-sm-5">{field.label}</div>
                                    <div className="col-sm-3">
                                        <label className="checkbox">
                                            <span>Include</span>
                                            <input type="checkbox" checked={field.visible} onChange={() => this.checkboxOnChange(field, 'visible')} />
                                        </label>
                                    </div>
                                    <div className="col-sm-3">
                                        <label className="checkbox">
                                            <span>Required</span>
                                            <input type="checkbox" checked={field.required} onChange={() => this.checkboxOnChange(field, 'required')} />
                                        </label>
                                    </div>
                                </div>
                            </li>
                        );
                    })}
                </ul>

                <div className="text-right">
                    <button className="btn btn-primary" type="button" onClick={() => this.saveSchemeOnClick()}>Save { this.state.activeFieldTab } Field Scheme</button>
                </div>
            </div>
        );
    }
};

export default LeadsAndCustomers;
